import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import DashboardLayout from './components/layout/DashboardLayout';
import LoginForm from './components/auth/LoginForm';
import RegisterForm from './components/auth/RegisterForm';
import MarketingLayout from './components/marketing/MarketingLayout';
import { Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import DashboardLoader from './components/common/DashboardLoader';
import ToastDisplay from './components/common/ToastProvider';
import FacilityRouteGuard from './components/routing/FacilityRouteGuard';
import RoleRouteGuard from './components/routing/RoleRouteGuard';
import { FacilityProvider } from './contexts/FacilityContext';
import TestingEnvBadge from './components/common/TestingEnvBadge';

// Lazy load route components for code splitting
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Users = lazy(() => import('./pages/Users'));
const Facilities = lazy(() => import('./pages/Facilities'));
const TeamManagement = lazy(() => import('./pages/TeamManagement'));
const RoleManagement = lazy(() => import('./pages/RoleManagement'));
const AccessManagement = lazy(() => import('./pages/AccessManagement'));
const FacilityUsers = lazy(() => import('./pages/facility/FacilityUsers'));
const FacilityTeamPerformance = lazy(() => import('./pages/facility/FacilityTeamPerformance'));
const FacilityResidents = lazy(() => import('./pages/facility/FacilityResidents'));
const ResidentDetail = lazy(() => import('./pages/residents/ResidentDetail'));
const FacilityContacts = lazy(() => import('./pages/facility/FacilityContacts'));
const FacilityDocuments = lazy(() => import('./pages/facility/FacilityDocuments'));
const FacilityTasks = lazy(() => import('./pages/facility/FacilityTasks'));
const FacilitySupplies = lazy(() => import('./pages/facility/FacilitySupplies'));
const FacilityPrnAudit = lazy(() => import('./pages/facility/PrnAudit'));
const TaskTemplates = lazy(() => import('./pages/facility/TaskTemplates'));
const FacilityInspections = lazy(() => import('./pages/facility/FacilityInspections'));
const Medications = lazy(() => import('./pages/Medications'));
const CarePlans = lazy(() => import('./pages/CarePlans'));
const Schedules = lazy(() => import('./pages/Schedules'));
const Profile = lazy(() => import('./pages/Profile'));
const NotificationCenter = lazy(() => import('./pages/NotificationCenter'));
const NotificationPreferences = lazy(() => import('./pages/NotificationPreferences'));

// Marketing site pages (public, unauthenticated)
const Home = lazy(() => import('./pages/marketing/Home'));
const Features = lazy(() => import('./pages/marketing/Features'));
const Pricing = lazy(() => import('./pages/marketing/Pricing'));
const Security = lazy(() => import('./pages/marketing/Security'));
const About = lazy(() => import('./pages/marketing/About'));
const RequestDemo = lazy(() => import('./pages/marketing/RequestDemo'));
const Contact = lazy(() => import('./pages/marketing/Contact'));

// Loading fallback component
const LoadingFallback: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '50vh',
    }}
  >
    <DashboardLoader size={80} minHeight="auto" />
  </Box>
);

const App: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  const content = isLoading ? (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <DashboardLoader size={80} minHeight="auto" />
      </Box>
  ) : !isAuthenticated ? (
      <>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route element={<MarketingLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/features" element={<Features />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/security" element={<Security />} />
              <Route path="/about" element={<About />} />
              <Route path="/request-demo" element={<RequestDemo />} />
              <Route path="/contact" element={<Contact />} />
            </Route>
            <Route path="/login" element={<LoginForm />} />
            <Route path="/register" element={<RegisterForm />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        <ToastDisplay />
      </>
  ) : (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FacilityProvider>
        <DashboardLayout>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route
              path="/users"
              element={
                <RoleRouteGuard routePath="/users">
                  <Users />
                </RoleRouteGuard>
              }
            />
            <Route
              path="/facilities"
              element={
                <RoleRouteGuard routePath="/facilities">
                  <Facilities />
                </RoleRouteGuard>
              }
            />
            <Route
              path="/team-management"
              element={
                <RoleRouteGuard routePath="/team-management">
                  <TeamManagement />
                </RoleRouteGuard>
              }
            />
            <Route
              path="/role-management"
              element={
                <RoleRouteGuard routePath="/role-management">
                  <RoleManagement />
                </RoleRouteGuard>
              }
            />
            <Route
              path="/access-management"
              element={
                <RoleRouteGuard routePath="/access-management">
                  <AccessManagement />
                </RoleRouteGuard>
              }
            />
            {/* Facility-scoped routes — roles + facility context from accessControl */}
            <Route
              path="/facility/residents"
              element={
                <FacilityRouteGuard routePath="/facility/residents">
                  <FacilityResidents />
                </FacilityRouteGuard>
              }
            />
            <Route
              path="/residents/:id"
              element={
                <FacilityRouteGuard routePath="/residents/:id">
                  <ResidentDetail />
                </FacilityRouteGuard>
              }
            />
            <Route
              path="/facility/contacts"
              element={
                <FacilityRouteGuard routePath="/facility/contacts">
                  <FacilityContacts />
                </FacilityRouteGuard>
              }
            />
            <Route
              path="/facility/documents"
              element={
                <FacilityRouteGuard routePath="/facility/documents">
                  <FacilityDocuments />
                </FacilityRouteGuard>
              }
            />
            <Route
              path="/facility/tasks"
              element={
                <FacilityRouteGuard routePath="/facility/tasks">
                  <FacilityTasks />
                </FacilityRouteGuard>
              }
            />
            <Route
              path="/facility/supplies"
              element={
                <FacilityRouteGuard routePath="/facility/supplies">
                  <FacilitySupplies />
                </FacilityRouteGuard>
              }
            />
            <Route
              path="/facility/prn-audit"
              element={
                <FacilityRouteGuard routePath="/facility/prn-audit">
                  <FacilityPrnAudit />
                </FacilityRouteGuard>
              }
            />
            <Route
              path="/facility/task-templates"
              element={
                <FacilityRouteGuard routePath="/facility/task-templates">
                  <TaskTemplates />
                </FacilityRouteGuard>
              }
            />
            <Route
              path="/facility/inspections"
              element={
                <FacilityRouteGuard routePath="/facility/inspections">
                  <FacilityInspections />
                </FacilityRouteGuard>
              }
            />
            <Route
              path="/facility/users"
              element={
                <FacilityRouteGuard routePath="/facility/users">
                  <FacilityUsers />
                </FacilityRouteGuard>
              }
            />
            <Route
              path="/facility/team-performance"
              element={
                <FacilityRouteGuard routePath="/facility/team-performance">
                  <FacilityTeamPerformance />
                </FacilityRouteGuard>
              }
            />
            {/* Healthcare-specific features */}
            <Route
              path="/medications"
              element={
                <RoleRouteGuard routePath="/medications">
                  <Medications />
                </RoleRouteGuard>
              }
            />
            <Route
              path="/care-plans"
              element={
                <RoleRouteGuard routePath="/care-plans">
                  <CarePlans />
                </RoleRouteGuard>
              }
            />
            <Route
              path="/schedules"
              element={
                <RoleRouteGuard routePath="/schedules">
                  <Schedules />
                </RoleRouteGuard>
              }
            />
            <Route
              path="/profile"
              element={<Profile />}
            />
            <Route path="/notifications" element={<NotificationCenter />} />
            <Route path="/notifications/preferences" element={<NotificationPreferences />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
        </DashboardLayout>
        <ToastDisplay />
      </FacilityProvider>
    </LocalizationProvider>
  );

  return (
    <>
      <TestingEnvBadge />
      {content}
    </>
  );
};

export default App;

// Ensure this file is treated as a module
export {};
